import React, { useState, useEffect, useRef } from 'react';
import { Lock, Eye, EyeOff, X, AlertCircle, Loader2, ShieldCheck } from 'lucide-react';
import { verifyAdminToken, setAdminToken } from '../lib/auth';

interface AdminLoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const AdminLoginModal: React.FC<AdminLoginModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [tokenInput, setTokenInput] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setTokenInput('');
      setErrorMsg(null);
      setIsLoading(false);
      setIsLocked(false);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLocked) return;

    const cleanToken = tokenInput.trim();
    if (!cleanToken) {
      setErrorMsg('Please enter your admin secret token.');
      return;
    }

    setIsLoading(true);
    setErrorMsg(null);

    const result = await verifyAdminToken(cleanToken);
    setIsLoading(false);

    if (result.success) {
      setAdminToken(cleanToken);
      onSuccess();
      onClose();
    } else {
      if (result.locked) {
        setIsLocked(true);
      }
      setErrorMsg(result.error || 'Invalid admin secret token. Please try again.');
    }
  };

  return (
    <div
      className="modal fade show d-block"
      tabIndex={-1}
      role="dialog"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.65)', backdropFilter: 'blur(4px)', zIndex: 1060 }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="modal-dialog modal-dialog-centered" role="document" style={{ maxWidth: '420px' }}>
        <div className="modal-content border shadow-lg rounded-4 overflow-hidden">
          {/* Header */}
          <div className="modal-header border-bottom px-4 py-3 bg-body-tertiary">
            <div className="d-flex align-items-center gap-2">
              <div className="p-2 rounded-circle bg-warning-subtle text-warning d-flex align-items-center justify-content-center">
                <Lock size={18} />
              </div>
              <h5 className="modal-title fs-6 fw-bold mb-0">Admin Authentication</h5>
            </div>
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={onClose}
              disabled={isLoading}
            />
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit}>
            <div className="modal-body p-4">
              <p className="text-secondary small mb-3">
                Enter your site <code className="fw-semibold">ADMIN_SECRET</code> to unlock in-page editing, CMS management, and site settings.
              </p>

              {errorMsg && (
                <div className="alert alert-danger d-flex align-items-center gap-2 py-2 px-3 small rounded-3 mb-3" role="alert">
                  <AlertCircle size={16} className="flex-shrink-0" />
                  <div>{errorMsg}</div>
                </div>
              )}

              <div className="mb-3">
                <label className="form-label small fw-semibold">Secret Passphrase / Token</label>
                <div className="input-group">
                  <input
                    ref={inputRef}
                    type={showPassword ? 'text' : 'password'}
                    className="form-control"
                    placeholder={isLocked ? 'Access Locked' : 'Enter admin secret...'}
                    value={tokenInput}
                    onChange={(e) => {
                      setTokenInput(e.target.value);
                      if (errorMsg) setErrorMsg(null);
                    }}
                    disabled={isLoading || isLocked}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowPassword((prev) => !prev)}
                    title={showPassword ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                    disabled={isLocked}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="modal-footer border-top px-4 py-3 bg-body-tertiary d-flex justify-content-between">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={onClose}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={`btn btn-sm ${isLocked ? 'btn-danger' : 'btn-warning'} d-flex align-items-center gap-2 fw-semibold px-3`}
                disabled={isLoading || isLocked}
              >
                {isLoading ? (
                  <>
                    <Loader2 size={16} className="spinner-border spinner-border-sm border-0" />
                    Verifying...
                  </>
                ) : isLocked ? (
                  <>
                    <Lock size={16} />
                    IP Locked Out
                  </>
                ) : (
                  <>
                    <ShieldCheck size={16} />
                    Unlock Admin Mode
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
